import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { ClipboardDocumentListIcon } from "@heroicons/react/24/outline";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { useProperty } from "../../../Contexts/PropertyContext/PropertyContext";
import { apiClient } from "../../../api";
import { baseUrl } from "../../../Environments/environment";

const WORKFLOW_REVIEW_PATH = "/dashboard/configure/workflow";
const PMS_PATH = "/dashboard/configure/pms";
const TWILIO_PATH = "/dashboard/configure/twilio";
const NOTIFICATION_PATH = "/dashboard/configure/notification";
const PRE_ARRIVAL_CHECKIN_PATH = `${WORKFLOW_REVIEW_PATH}#pre-arrival-check-in`;
const WORKFLOW_ADDONS_PATH = `${WORKFLOW_REVIEW_PATH}#workflow-addons-flow`;
const WORKFLOW_AMENITIES_PATH = `${WORKFLOW_REVIEW_PATH}#workflow-amenities`;

/** Pending rows that show View + Mark as completed in the drawer (UI only for Mark). */
const ITEM_IDS_WITH_PANEL_ACTION_BUTTONS = new Set([
	"workflowAmenities",
	"workflowAddOns",
]);

function isDashboardHomePath(pathname) {
	if (!pathname) return false;
	const normalized = pathname.replace(/\/+$/, "") || "/";
	return normalized === "/dashboard";
}

function hasText(value) {
	return value !== undefined && value !== null && String(value).trim() !== "";
}

function isWorkflowConfigured(workflow) {
	if (!workflow || typeof workflow !== "object") return false;
	const flowEntries = Object.entries(workflow).filter(
		([key, value]) => key.toLowerCase().includes("flow") && value && typeof value === "object",
	);
	const anyFlowEnabled = flowEntries.some(([, flow]) => {
		if (flow.enabled === true || flow.reviewsEnabled === true) return true;
		return Object.values(flow).some((v) => v === true);
	});
	return anyFlowEnabled || flowEntries.length > 0;
}

function hasAtLeastOnePreArrivalPolicy(workflow) {
	const policies = workflow?.preArrivalFlow?.propertyPolicies;
	if (!Array.isArray(policies)) return false;
	return policies.some(
		(policy) => hasText(policy?.name) || hasText(policy?.description),
	);
}

function hasAtLeastOneAmenitySelected(workflow) {
	const amenities = workflow?.homeFlow?.amenities;
	if (!Array.isArray(amenities)) return false;
	return amenities.some((a) => a?.enabled === true);
}

function customAddOnHasContent(addOn) {
	if (!addOn || typeof addOn !== "object") return false;
	return hasText(addOn.name) || hasText(addOn.description);
}

/** True when Add-Ons Flow has at least one custom add-on with a name or description. */
function hasAtLeastOneWorkflowAddOnConfigured(workflow) {
	const list = workflow?.addOnsFlow?.customAddOns;
	if (!Array.isArray(list)) return false;
	return list.some(customAddOnHasContent);
}

function isPmsConfigured(setting) {
	if (!setting || !setting.pmsEnabled) return false;
	if (!hasText(setting.pmsType) || !hasText(setting.pmsId)) return false;
	if (setting.pmsType === "asiPms") {
		return hasText(setting.asiApplicationId) && hasText(setting.asiSecurityKey);
	}
	return true;
}

function isNotificationConfigured(addOnsNotification) {
	if (!Array.isArray(addOnsNotification)) return false;
	return addOnsNotification.some((addOn) => {
		if (!addOn?.enabled) return false;
		const hasEmail = Array.isArray(addOn.emails)
			&& addOn.emails.some((email) => hasText(email));
		const hasPhone = Array.isArray(addOn.phoneNumbers)
			&& addOn.phoneNumbers.some((p) => hasText(p?.phoneNumber));
		return hasEmail || hasPhone;
	});
}

function getPropertySetupStatus(propertySetup) {
	return {
		amenitiesSetup: Boolean(propertySetup?.amenitiesSetup),
		addOnsSetup: Boolean(propertySetup?.addOnsSetup),
	};
}

async function fetchSetupStatus(propertyId) {
	const [workflowRes, settingRes, twilioRes, notificationRes, propertySetupRes] =
		await Promise.allSettled([
			apiClient.get(`${baseUrl}/workflow/${propertyId}`),
			apiClient.get(`${baseUrl}/setting/${propertyId}`),
			apiClient.get(`${baseUrl}/twilio/${propertyId}/isTwilioSetup`),
			apiClient.get(`/addOnsNotification/${propertyId}`),
			apiClient.get(`${baseUrl}/property/${propertyId}/setup`),
		]);

	return {
		workflow:
			workflowRes.status === "fulfilled"
				? workflowRes.value?.data?.result ?? null
				: null,
		setting:
			settingRes.status === "fulfilled"
				? settingRes.value?.data?.result?.setting ?? null
				: null,
		twilio:
			twilioRes.status === "fulfilled"
				? twilioRes.value?.data?.result ?? null
				: null,
		addOnsNotification:
			notificationRes.status === "fulfilled"
				? notificationRes.value?.data?.result?.addOnNotifications ?? null
				: null,
		propertySetup:
			propertySetupRes.status === "fulfilled"
				? propertySetupRes.value?.data?.result?.propertySetup ?? null
				: null,
	};
}

export default function ProfileSetupPendingPanel() {
	const { selectedPropertyId } = useProperty();
	const location = useLocation();
	const queryClient = useQueryClient();
	const [drawerOpen, setDrawerOpen] = useState(() =>
		typeof window !== "undefined"
		&& isDashboardHomePath(window.location.pathname),
	);
	const { data, isPending } = useQuery({
		queryKey: ["profileSetupStatus", selectedPropertyId],
		queryFn: () => fetchSetupStatus(selectedPropertyId),
		enabled: Boolean(selectedPropertyId),
		staleTime: 60_000,
	});
	const markSetupCompleteMutation = useMutation({
		mutationFn: (payload) =>
			apiClient.put(`${baseUrl}/property/${selectedPropertyId}/setup`, payload),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["profileSetupStatus", selectedPropertyId],
			});
		},
	});

	const handleMarkAsCompleted = (itemId) => {
		if (!selectedPropertyId) return;
		const current = getPropertySetupStatus(data?.propertySetup);
		let payload = null;
		if (itemId === "workflowAmenities") {
			payload = { ...current, amenitiesSetup: true };
		} else if (itemId === "workflowAddOns") {
			payload = { ...current, addOnsSetup: true };
		}
		if (!payload) return;
		markSetupCompleteMutation.mutate(payload);
	};

	const setupSummary = useMemo(() => {
		const propertySetup = getPropertySetupStatus(data?.propertySetup);
		const checks = [
			{
				id: "workflow",
				label: "All Workflow setup items",
				hint: "Complete all required settings in Workflow.",
				to: WORKFLOW_REVIEW_PATH,
				complete: isWorkflowConfigured(data?.workflow),
			},
			{
				id: "workflowAmenities",
				label: "Amenities",
				hint: "In Configure → Workflow → Reservation Confirmed, select at least one amenity.",
				to: WORKFLOW_AMENITIES_PATH,
				complete: propertySetup.amenitiesSetup,
			},
			{
				id: "workflowAddOns",
				label: "Add-ons (Workflow)",
				hint: "In Configure → Workflow → Add-Ons Flow, add at least one custom add-on (name or description filled in).",
				to: WORKFLOW_ADDONS_PATH,
				complete: propertySetup.addOnsSetup,
			},
			{
				id: "preArrivalCheckIn",
				label: "Pre-arrival check-in",
				hint: "Add at least one property policy under Pre Arrival Check In (Property Policies).",
				to: PRE_ARRIVAL_CHECKIN_PATH,
				complete: hasAtLeastOnePreArrivalPolicy(data?.workflow),
			},
			{
				id: "pms",
				label: "PMS Integration",
				hint: "Connect PMS and verify credentials.",
				to: PMS_PATH,
				complete: isPmsConfigured(data?.setting),
			},
			{
				id: "twilio",
				label: "Twilio Setup",
				hint: "Complete Twilio phone setup and verification.",
				to: TWILIO_PATH,
				complete: Boolean(data?.twilio?.isComplete),
			},
			{
				id: "notification",
				label: "Notification",
				hint: "Enable notifications and add at least one destination.",
				to: NOTIFICATION_PATH,
				complete: isNotificationConfigured(data?.addOnsNotification),
			},
		];

		const totalChecks = checks.length;
		const items = checks.map((check) => ({
			id: check.id,
			label: check.label,
			hint: check.hint,
			to: check.to,
			complete: check.complete,
			panelActionButtons: ITEM_IDS_WITH_PANEL_ACTION_BUTTONS.has(check.id),
		}));
		const pendingItems = items.filter((item) => !item.complete);
		const pendingChecks = pendingItems.length;
		const completedChecks = Math.max(totalChecks - pendingChecks, 0);
		const completedPercent =
			totalChecks > 0
				? Math.round((completedChecks / totalChecks) * 100)
				: 0;
		const pendingPercent =
			totalChecks > 0 ? Math.max(100 - completedPercent, 0) : 0;

		return {
			items,
			pendingItems,
			totalChecks,
			pendingChecks,
			completedChecks,
			completedPercent,
			pendingPercent,
		};
	}, [data]);

	useEffect(() => {
		if (!isDashboardHomePath(location.pathname)) {
			setDrawerOpen(false);
		}
	}, [location.pathname]);

	useEffect(() => {
		if (!drawerOpen) return;
		const onKey = (e) => {
			if (e.key === "Escape") setDrawerOpen(false);
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [drawerOpen]);

	if (!selectedPropertyId || isPending) {
		return null;
	}

	if (setupSummary.pendingItems.length === 0) {
		return null;
	}

	const count = setupSummary.pendingItems.length;
	const isMarkingSetupComplete = markSetupCompleteMutation.isPending;

	const panel = (
		<>
			<button
				type="button"
				onClick={() => setDrawerOpen(true)}
				className="fixed right-0 top-[28%] z-[80] flex -translate-y-1/2 flex-col items-center gap-0.5 rounded-l-lg border border-r-0 border-gray-200 bg-white py-2 pl-2 pr-1 shadow-md transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 sm:top-[22%] sm:py-2.5"
				aria-label={`Pending setup, ${count} items`}
				aria-expanded={drawerOpen}
				aria-haspopup="dialog"
			>
				<ClipboardDocumentListIcon
					className="h-5 w-5 shrink-0 text-indigo-600"
					aria-hidden
				/>
				<span className="inline-flex min-h-[1.25rem] min-w-[1.25rem] items-center justify-center rounded-full bg-amber-100 px-1 text-[10px] font-semibold text-amber-900">
					{count}
				</span>
			</button>

			{drawerOpen ? (
				<>
					<button
						type="button"
						className="fixed inset-0 z-[90] bg-black/30"
						aria-label="Close pending setup"
						onClick={() => setDrawerOpen(false)}
					/>
					<aside
						className="fixed right-2 top-3 z-[100] flex w-[min(26rem,calc(100vw-1rem))] max-h-[calc(100dvh-1.5rem)] max-w-[26rem] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl sm:right-4 sm:top-6 sm:max-h-[calc(100dvh-3rem)] sm:w-[22rem] sm:max-w-[22rem]"
						role="dialog"
						aria-modal="true"
						aria-labelledby="pending-setup-title"
					>
						<div className="flex shrink-0 items-center justify-between gap-3 border-b border-gray-100 bg-gray-50 px-4 py-3 sm:px-5 sm:py-4">
							<div className="min-w-0">
								<p
									id="pending-setup-title"
									className="truncate text-sm font-bold text-gray-900 sm:text-base"
								>
									Pending setup
								</p>
								<p className="mt-0.5 text-xs text-gray-500">
									{setupSummary.completedChecks}/{setupSummary.totalChecks} completed
								</p>
							</div>
							<button
								type="button"
								onClick={() => setDrawerOpen(false)}
								className="shrink-0 rounded-md p-1 text-gray-500 hover:bg-gray-200 hover:text-gray-800"
								aria-label="Close"
							>
								<XMarkIcon className="h-5 w-5" aria-hidden />
							</button>
						</div>

						<div className="hide-scrollbar min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-5 sm:py-5">
							<div className="mb-4 rounded-xl border border-indigo-100 bg-indigo-50/60 p-4">
								<p className="text-xs font-semibold text-gray-900">Setup stats</p>
								<div className="mt-2 flex items-center gap-3">
									<div
										className="relative h-14 w-14 shrink-0 rounded-full"
										style={{
											background: `conic-gradient(#4f46e5 0 ${setupSummary.completedPercent}%, #f59e0b ${setupSummary.completedPercent}% 100%)`,
										}}
									>
										<div className="absolute inset-[5px] grid place-items-center rounded-full bg-white text-[10px] font-semibold text-gray-700">
											{setupSummary.completedPercent}%
										</div>
									</div>
									<div className="min-w-0 flex-1 text-[11px] text-gray-700">
										<p>
											<span className="font-semibold text-gray-900">
												{setupSummary.completedChecks}
											</span>{" "}
											completed
										</p>
										<p>
											<span className="font-semibold text-gray-900">
												{setupSummary.pendingChecks}
											</span>{" "}
											pending
										</p>
									</div>
								</div>
								<div className="mt-3">
									<div className="h-2 w-full overflow-hidden rounded-full bg-indigo-100">
										<div
											className="h-full rounded-full bg-indigo-600 transition-all"
											style={{ width: `${setupSummary.completedPercent}%` }}
										/>
									</div>
									<p className="mt-1 text-[10px] text-gray-600">
										{setupSummary.completedPercent}% complete -{" "}
										{setupSummary.pendingPercent}% pending
									</p>
								</div>
							</div>

							<ul className="space-y-3">
								{setupSummary.items.map((item) => {
									const StatusBadge = (
										<span
											className={
												item.complete
													? "inline-flex shrink-0 items-center rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-semibold text-green-800"
													: "inline-flex shrink-0 items-center rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-900"
											}
										>
											{item.complete ? "Completed" : "Pending"}
										</span>
									);

									const pendingCardClass =
										"block rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm sm:px-5 sm:py-4";
									const pendingLinkClass = `${pendingCardClass} transition hover:border-indigo-300 hover:bg-indigo-50/40 hover:shadow`;

									const cardInner = (
										<>
											<div className="flex items-start justify-between gap-2">
												<p className="text-xs font-medium leading-snug text-gray-900">
													{item.label}
												</p>
												{StatusBadge}
											</div>
											<p className="mt-0.5 line-clamp-3 text-[11px] leading-snug text-gray-600">
												{item.hint}
											</p>
											{!item.complete && !item.panelActionButtons && (
												<span className="mt-1 inline-block text-[11px] font-medium text-indigo-600">
													Complete now
												</span>
											)}
											{!item.complete && item.panelActionButtons && (
												<div className="mt-2 flex flex-wrap gap-2">
													<Link
														to={item.to ?? WORKFLOW_REVIEW_PATH}
														onClick={() => setDrawerOpen(false)}
														className="inline-flex items-center justify-center rounded-md border border-indigo-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-indigo-700 shadow-sm hover:bg-indigo-50"
													>
														View
													</Link>
													<button
														type="button"
														onClick={() => handleMarkAsCompleted(item.id)}
														disabled={isMarkingSetupComplete}
														className="inline-flex items-center justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-[11px] font-semibold text-white shadow-sm hover:bg-indigo-700"
													>
														{isMarkingSetupComplete
															? "Marking..."
															: "Mark as completed"}
													</button>
												</div>
											)}
										</>
									);

									return (
										<li key={item.id}>
											{item.complete ? (
												<div
													className="block rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 shadow-sm sm:px-5 sm:py-4"
													aria-label={`${item.label} completed`}
												>
													{cardInner}
												</div>
											) : item.panelActionButtons ? (
												<div className={pendingCardClass}>{cardInner}</div>
											) : (
												<Link
													to={item.to ?? WORKFLOW_REVIEW_PATH}
													onClick={() => setDrawerOpen(false)}
													className={pendingLinkClass}
												>
													{cardInner}
												</Link>
											)}
										</li>
									);
								})}
							</ul>
						</div>
					</aside>
				</>
			) : null}
		</>
	);

	return createPortal(panel, document.body);
}
